import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/content";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [active, setActive] = useState(0);
  const total = movies.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => setActive((v) => (v + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const go = (dir: number) => setActive((v) => (v + dir + total) % total);

  return (
    <section className="relative h-[440px] w-full overflow-hidden rounded-lg bg-primary-950 md:h-[560px]">
      {movies.map((movie, idx) => (
        <div
          key={movie.id}
          className={[
            "absolute inset-0 transition-opacity duration-700 ease-out",
            idx === active ? "opacity-100" : "pointer-events-none opacity-0",
          ].join(" ")}
        >
          <img
            src={movie.backdrop || movie.cover}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 欧美三级影视详情`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/45 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative flex h-full w-full flex-col justify-end px-6 pb-12 md:px-14 md:pb-16">
        <span className="mb-3 flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
          <i className="ri-fire-line"></i>
          热门推荐
        </span>
        <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
          {movies[active].title}
        </h2>
        <p className="mt-1 text-sm text-primary-100/80 md:text-base">{movies[active].titleEn}</p>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
          {movies[active].summary}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={`/detail/${movies[active].id}`}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
          >
            <i className="ri-play-circle-line text-lg"></i>
            查看详情
          </Link>
          <Link
            to="/category"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
          >
            <i className="ri-grid-line text-lg"></i>
            浏览全部影片
          </Link>
        </div>
      </div>

      <div className="absolute bottom-5 right-5 flex items-center gap-3 md:bottom-8 md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(-1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <div className="flex items-center gap-1.5">
          {movies.map((movie, idx) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到第 ${idx + 1} 张`}
              onClick={() => setActive(idx)}
              className={[
                "h-1.5 cursor-pointer rounded-full transition-all duration-300",
                idx === active ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80",
              ].join(" ")}
            ></button>
          ))}
        </div>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}